/**
 * Decision-tree diagram helping a buyer choose between disposable and reusable
 * shipment trackers based on lane characteristics. Pure SVG.
 */
import React from "react";

export default function DecisionTree({ title = "Choosing a tracker — decision tree" }) {
  return (
    <figure
      className="my-10 border border-line bg-white p-6 lg:p-8 overflow-x-auto"
      data-testid="decision-tree-diagram"
    >
      <figcaption className="font-mono text-[11px] uppercase tracking-widest text-ink-subtle mb-6">
        {title}
      </figcaption>
      <svg
        viewBox="0 0 900 480"
        role="img"
        aria-label="Decision tree diagram helping buyers choose between disposable and reusable trackers based on lane, volume, journey duration and recovery model"
        className="w-full h-auto min-w-[720px]"
      >
        {/* Root */}
        <rect x="360" y="16" width="180" height="52" rx="4" fill="#0A0A0A" />
        <text x="450" y="42" textAnchor="middle" fontSize="12" fill="#fff" fontFamily="ui-monospace, SFMono-Regular, monospace">START</text>
        <text x="450" y="58" textAnchor="middle" fontSize="10" fill="#fff" fontFamily="ui-monospace, SFMono-Regular, monospace">Is the lane closed-loop?</text>

        {/* Line down/left/right */}
        <line x1="450" y1="68" x2="450" y2="90" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="220" y1="90" x2="680" y2="90" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="220" y1="90" x2="220" y2="110" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="680" y1="90" x2="680" y2="110" stroke="#0A0A0A" strokeWidth="1" />
        <text x="290" y="86" fontSize="10" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">YES · high recovery</text>
        <text x="510" y="86" fontSize="10" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">NO · one-way / low recovery</text>

        {/* Level 2 — YES branch */}
        <rect x="120" y="110" width="200" height="52" rx="4" fill="#fff" stroke="#0A0A0A" />
        <text x="220" y="132" textAnchor="middle" fontSize="11" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">Long-duration lanes?</text>
        <text x="220" y="148" textAnchor="middle" fontSize="10" fill="#525252">weeks / rechargeable</text>

        {/* Level 2 — NO branch */}
        <rect x="580" y="110" width="200" height="52" rx="4" fill="#fff" stroke="#0A0A0A" />
        <text x="680" y="132" textAnchor="middle" fontSize="11" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">Location precision needs?</text>
        <text x="680" y="148" textAnchor="middle" fontSize="10" fill="#525252">venue-level vs GPS-grade</text>

        {/* Level 2 lines */}
        <line x1="220" y1="162" x2="220" y2="200" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="120" y1="200" x2="320" y2="200" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="120" y1="200" x2="120" y2="230" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="320" y1="200" x2="320" y2="230" stroke="#0A0A0A" strokeWidth="1" />

        <line x1="680" y1="162" x2="680" y2="200" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="580" y1="200" x2="780" y2="200" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="580" y1="200" x2="580" y2="230" stroke="#0A0A0A" strokeWidth="1" />
        <line x1="780" y1="200" x2="780" y2="230" stroke="#0A0A0A" strokeWidth="1" />

        <text x="160" y="196" fontSize="10" fill="#525252">YES</text>
        <text x="290" y="196" fontSize="10" fill="#525252">NO</text>
        <text x="616" y="196" fontSize="10" fill="#525252">BLE / venue</text>
        <text x="732" y="196" fontSize="10" fill="#525252">GNSS / cellular</text>

        {/* Level 3 outcomes */}
        {/* Reusable */}
        <rect x="20" y="230" width="200" height="70" rx="4" fill="#0047AB" />
        <text x="120" y="256" textAnchor="middle" fontSize="12" fill="#fff" fontFamily="ui-monospace, SFMono-Regular, monospace">REUSABLE TRACKER</text>
        <text x="120" y="274" textAnchor="middle" fontSize="10" fill="#fff">Amortise CapEx over trips;</text>
        <text x="120" y="288" textAnchor="middle" fontSize="10" fill="#fff">recovery loop worth it.</text>

        {/* Hybrid */}
        <rect x="240" y="230" width="200" height="70" rx="4" fill="#fff" stroke="#0047AB" strokeWidth="2" />
        <text x="340" y="256" textAnchor="middle" fontSize="12" fill="#0047AB" fontFamily="ui-monospace, SFMono-Regular, monospace">HYBRID SETUP</text>
        <text x="340" y="274" textAnchor="middle" fontSize="10" fill="#525252">Reusable on backbone lanes;</text>
        <text x="340" y="288" textAnchor="middle" fontSize="10" fill="#525252">disposable at last leg.</text>

        {/* BLE disposable */}
        <rect x="480" y="230" width="200" height="70" rx="4" fill="#0A0A0A" />
        <text x="580" y="256" textAnchor="middle" fontSize="12" fill="#fff" fontFamily="ui-monospace, SFMono-Regular, monospace">BLE SMART LABEL</text>
        <text x="580" y="274" textAnchor="middle" fontSize="10" fill="#fff">Thin, infrastructure-light;</text>
        <text x="580" y="288" textAnchor="middle" fontSize="10" fill="#fff">works via partner network.</text>

        {/* Cellular disposable */}
        <rect x="700" y="230" width="200" height="70" rx="4" fill="#0A0A0A" />
        <text x="800" y="256" textAnchor="middle" fontSize="12" fill="#fff" fontFamily="ui-monospace, SFMono-Regular, monospace">CELLULAR + GNSS</text>
        <text x="800" y="274" textAnchor="middle" fontSize="10" fill="#fff">Own-signal accuracy;</text>
        <text x="800" y="288" textAnchor="middle" fontSize="10" fill="#fff">condition sensors + coverage.</text>

        {/* Footer note */}
        <text x="20" y="360" fontSize="10" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">
          Also consider — shipment value, sensor requirements (temp/shock/light), annual volume, unit economics, and airline restrictions where applicable.
        </text>
        <text x="20" y="380" fontSize="10" fill="#525252">
          The tree is directional — a real programme often combines architectures across lane segments.
        </text>
      </svg>
    </figure>
  );
}
